/**
 * O CATÁLOGO DE MÓDULOS — uma lista, um lugar.
 *
 * ═══ POR QUE ISTO EXISTE (medido em 20-21/08/2026, achado R3 do Operador) ═══
 * A casa tinha TRÊS listas de módulos que não batiam entre si:
 *
 *   · `components/operador/formatos.ts` — o que o BOTÃO do Operador oferece
 *   · `routes/operador.ts` (MODULO_ROTAS)  — o que a ADOÇÃO mede
 *   · `client/lib/nav.ts` (moduloDoItem)   — o que o MENU esconde
 *
 * O estrago, medido no banco (`empresas.modulos`, 5 empresas ativas):
 *   🟠 `boletos`, `colaboradores` e `diagnostico` ESCONDEM tela de verdade e não
 *      tinham botão no painel — o dono não conseguia ligar nem desligar, e a
 *      adoção não os media (Átomo, M5 e vitrine-fac têm dois deles OFF);
 *   🟠 `estoque` era oferecido e medido, mas não escondia item nenhum;
 *   🟥 `regularizacao` está GRAVADA em 5 empresas e nenhuma das três listas lia;
 *   🟥 `compliance`, `comunicacao` e `documentos` eram oferecidos e NUNCA foram
 *      gravados por ninguém — o botão "SST / Compliance" não escondia, não
 *      bloqueava e nem chegava a gravar.
 *
 * ═══ A REGRA QUE ESTE ARQUIVO IMPÕE ═══
 * **Módulo é o que ESCONDE ou BLOQUEIA alguma coisa.** Se desligar não tira tela
 * do menu nem fecha rota da API, não é módulo — é rótulo. Vender rótulo foi
 * exatamente o defeito: o Operador anunciava "22 módulos ociosos" e clicar em
 * alguns não fazia nada.
 *
 * ═══ COMO O CONTEÚDO FOI DECIDIDO ═══
 * As rotas NÃO foram escritas à mão: saíram da NAV real, derivadas com a lógica
 * que o menu já usava (`scripts/__deriva_catalogo.mts`). Escrever à mão é como as
 * três listas nasceram divergentes. O teste de PARIDADE trava item a item que
 * nenhum item do menu mudou de módulo nesta troca.
 */

export interface Modulo {
  /** Chave gravada em `empresas.modulos` e em `empresa_users.modulos`. */
  chave: string;
  /** Como o dono lê no painel do Operador. */
  label: string;
  /** Telas do cliente que este módulo controla (menu + medição de adoção). */
  rotas: readonly string[];
  /**
   * Rotas que o módulo controla e que NÃO são item de menu: deep-link, favorito
   * antigo, tela alcançável só por URL.
   *
   * 🟥 ISTO NASCEU DE UM DEFEITO MEU (21/08). O catálogo foi derivado da NAV, e a
   * NAV não tem `/pessoas`, `/tributario` nem `/estoque` — os três estavam no mapa
   * ANTIGO e sumiram na conversão. Rota que sai do mapa vira NÚCLEO: sempre
   * visível, fora da allow-list do grant. Ou seja, eu ia afrouxar a permissão de
   * três telas em silêncio, e a prova de paridade não veria, porque ela itera a
   * NAV — e essas rotas não estão lá. Quem pegou foi o guardrail
   * `pessoas-v2-nao-perde-nada`, que testa `/pessoas` NOMINALMENTE.
   */
  aliases: readonly string[];
  /** Prefixos da API que este módulo BLOQUEIA hoje (403 quando não contratado). */
  api: readonly string[];
  /** Vem no plano base — não é adicional cobrado à parte. */
  nucleo?: boolean;
  /**
   * INCLUSO na marca (05/09, raio-x bloqueio 10): esconde tela como qualquer módulo, o
   * Operador liga/desliga, mas NÃO conta como adicional no preço nem entra nos presets
   * "Só Núcleo"/"Ativar adicionais". São as telas que antes eram escondidas POR SLUG
   * (Painel só M5, Torre só Regula…) e agora são dado por empresa.
   */
  basico?: boolean;
  /** Existe só pra não sumir em silêncio: gravado no banco, sem nada pra gatear. */
  legado?: boolean;
}

/**
 * ⚠️ SOBRE O CAMPO `api`: ele lista o que BLOQUEIA HOJE, não o que "deveria".
 * Só entram prefixos que passaram por 6 semanas de log-only antes do flip
 * (#1733). Módulo com `api: []` NÃO tem portão de servidor — o menu esconde e
 * `curl` continua servindo. Encher isto no chute seria trocar "cosmético de
 * menu" por "derruba cliente que ninguém mediu"; o caminho é instrumentar em
 * log-only, medir, e só então mover pra cá.
 */
/**
 * ═══ 05/09 — O MENU DEIXOU DE ESCONDER POR SLUG (raio-x do Regula, bloqueio 10, passo 3) ═══
 * 46 itens do menu tinham `empresas: [slug, …]` — uma lista por item, deny-by-default, que
 * o Operador não via nem vendia. Agora TUDO que esconde tela é módulo: as 12 chaves novas
 * abaixo (nomes aprovados pelo dono em 05/09) reproduzem, item a item, o que cada empresa
 * via pela lista — a matriz por empresa está na migration `*_menu_por_modulo_matriz` e a
 * prova de paridade em `tests/menu-por-modulo-paridade.test.ts`.
 *
 * 🔑 A SEMÂNTICA MUDOU JUNTO: a lista era deny-by-default, `modulos` era allow-by-default
 * (só `false` escondia). Com a matriz completa gravada, o gate do menu passou a exigir
 * `true` quando a empresa TEM `modulos` (empresa nova só vê o que estiver marcado — é o
 * que quem vende módulo espera); empresa sem a coluna (null) continua vendo tudo.
 */
export const CATALOGO: readonly Modulo[] = [
  {
    chave: "comercial",
    label: "Comercial",
    nucleo: true,
    // Pipeline, Clientes e Contratos: todas as empresas. O que era por slug virou módulo
    // próprio (orcamentos, comercial_regula, mapa_carteira) — ver o bloco de 05/09 acima.
    rotas: ["/pipeline", "/clientes", "/contratos"],
    aliases: [],
    api: [],
  },
  { chave: "orcamentos", label: "Orçamentos", basico: true, rotas: ["/orcamentos"], aliases: [], api: [] },
  { chave: "comercial_regula", label: "Comercial Regula", basico: true, rotas: ["/simulador-proposta", "/mapa-cobertura"], aliases: [], api: [] },
  // `/mapa-da-carteira` (30/08) é a carteira da M5 desenhada no mapa; só ela tem.
  { chave: "mapa_carteira", label: "Mapa da carteira", basico: true, rotas: ["/mapa-da-carteira"], aliases: [], api: [] },
  // 👑 13/09: a Prospecção (12/09) saiu de `mapa_carteira` e virou módulo próprio — "deve aparecer apenas para
  // M5 e Regula". O banco já só abre a base para as duas (`prospeccao_liberada()`); o menu passa a coincidir.
  { chave: "prospeccao", label: "Prospecção", basico: true, rotas: ["/prospeccao"], aliases: [], api: [] },
  { chave: "painel", label: "Painel de operação", basico: true, rotas: ["/"], aliases: [], api: [] },
  { chave: "torre", label: "Torre de Controle", basico: true, rotas: ["/portfolio"], aliases: [], api: [] },
  { chave: "comunicacao", label: "Comunicação", basico: true, rotas: ["/comunicacao", "/whatsapp"], aliases: [], api: [] },
  { chave: "pessoas", label: "Pessoas e acesso", basico: true, rotas: ["/usuarios", "/folha"], aliases: [], api: [] },
  { chave: "societario", label: "Sócios", basico: true, rotas: ["/socios"], aliases: [], api: [] },
  { chave: "base", label: "Base técnica", basico: true, rotas: ["/engenharia", "/catalogo-servicos", "/plano-acoes"], aliases: [], api: [] },
  { chave: "sistema", label: "Sobre o sistema", basico: true, rotas: ["/sobre"], aliases: [], api: [] },
  { chave: "biblioteca", label: "Biblioteca normativa", basico: true, rotas: ["/biblioteca"], aliases: [], api: [] },
  { chave: "faturamento", label: "Faturamento Regula", basico: true, rotas: ["/faturamento-regula"], aliases: [], api: [] },
  // 👑 10/10 (mock 12): a tela LinkedIn é a fatia da página Regula360 na leitura semanal — só a regula360 a tem (a rota devolve 403 às outras).
  { chave: "linkedin", label: "LinkedIn da Regula", basico: true, rotas: ["/linkedin-regula"], aliases: [], api: [] },
  {
    chave: "operacao",
    label: "Operação",
    nucleo: true,
    rotas: ["/obras", "/cronograma", "/medicoes", "/saude-manutencao", "/checklists", "/acesso", "/prestadores", "/ativos"],
    aliases: [],
    api: [],
  },
  {
    chave: "financeiro",
    label: "Financeiro",
    nucleo: true,
    rotas: ["/painel-dinheiro", "/contas-receber", "/contas-pagar", "/ledger", "/fechamento", "/razao", "/conciliacao"], // /faturamento-regula virou `faturamento` (05/09) · /conciliacao ganhou item de menu em 12/09 (nunca teve)
    // Alcançáveis só por URL: o endereço em que a tela nova nasceu (a Duda já mandou links dele no zap) e a casa
    // da tela de dois lados, que ainda guarda fechar-o-caixa e os lotes de extrato.
    aliases: ["/conciliacao-lista"],
    api: [],
  },
  {
    chave: "fiscal",
    label: "Fiscal & Tributário",
    rotas: ["/fiscal"],
    aliases: ["/tributario"],
    api: ["/fiscal", "/tributario"],
  },
  {
    // ⚠️ O NOME MENTIA (14/07): chamava-se "Compliance / NR", o MESMO nome da tela
    // /compliance, que está INATIVA. Quem olhasse a lista pra decidir o que o
    // cliente contrata pensaria "é aquela tela desativada" e DESLIGARIA — derrubando
    // COLABORADORES junto, que é a viva e é quem consome /colaboradores/* na API.
    chave: "colaboradores",
    label: "Colaboradores (NR, ASO, laudos)",
    rotas: ["/colaboradores"],
    // /pessoas é o endereço que combina com o rótulo; /colaboradores continua
    // valendo pros favoritos e links antigos. As DUAS caem no mesmo módulo.
    aliases: ["/pessoas"],
    api: ["/colaboradores"],
  },
  {
    chave: "bi",
    label: "BI do site", // 👑 06/09 "6. ok": era "Indicadores do Site"
    rotas: ["/bi-site"],
    aliases: [],
    api: ["/bi"],
  },
  {
    // Sem item de menu próprio: quem consome é a API (materiais dentro de Obras).
    // Fica vendável porque o portão de servidor existe e o dono desliga de verdade.
    chave: "estoque",
    label: "Estoque",
    rotas: [],
    aliases: ["/estoque"],
    api: ["/estoque"],
  },
  {
    chave: "rh",
    label: "Folha & Ponto",
    rotas: ["/rh"],
    aliases: [],
    api: ["/folha"],
  },
  {
    chave: "portal",
    label: "Portal do Cliente",
    rotas: ["/portal"],
    aliases: [],
    api: [],
  },
  {
    chave: "auditoria",
    label: "Conformidade documental", // 👑 06/09 "6. ok": era "Gestão Regulatória"
    rotas: ["/fontes-auditoria", "/achados", "/risco-interdicao", "/autos-infracao", "/auditoria-do-contrato", "/radar-reforma", "/portal-economia"], // /risco-interdicao (D5, 10/10), /autos-infracao (D8, 11/10), /auditoria-do-contrato (D9, 11/10) e /radar-reforma (D10, 11/10): cabem no pilar da auditoria, sem módulo novo
    aliases: [],
    api: [],
  },
  {
    // 🟠 ESCONDIA TELA E NÃO TINHA BOTÃO. Três empresas já estão com ele OFF —
    // alguém gravou por script ou pela API, porque o painel não oferecia.
    chave: "diagnostico",
    label: "Diagnóstico & Monitoramento",
    rotas: ["/centros", "/onboarding-regula", "/mesa-revisao", "/a-confirmar", "/documentos-regula", "/requisitos-conformidade", "/portal-vencimentos", "/portal-comparar", "/portal-o-que-mudou", "/portal-board", "/api-integracoes", "/renovacoes-calendario", "/roteiros-renovacao", "/carteira-seguradora", "/esg-regula"], /* /esg-regula (mock 45, 14/10): o recorte ESG do acervo e das contas, no pilar-base */ // /portal-board (mock 13, 14/10): o painel do board do decisor, no pilar-base · /carteira-seguradora (mock 03, 14/10): nota documental e papel para a seguradora, no pilar-base · /renovacoes-calendario e /roteiros-renovacao (telas 07 e 10, mocks de 14/10): pilar-base da Regula, sem módulo novo · /api-integracoes (D12, 11/10): a configuração da API pública mora no pilar-base da Regula, sem módulo novo
    aliases: [],
    api: [],
  },
  {
    // 🟠 mesma história do `diagnostico`.
    chave: "boletos",
    label: "Auditoria de contas", // 👑 06/09 "6. ok": era "Auditoria de Boletos" (o produto na proposta mantém o nome)
    rotas: ["/portal-boletos", "/central-boletos"],
    aliases: [],
    api: [],
  },
  {
    // 🟥 ÓRFÃ COM DADO: gravada em 5 empresas (3 delas com `false`) e nenhuma das
    // três listas lia. Fica aqui como LEGADO em vez de sumir: chave gravada que o
    // código não conhece é como a gente descobre, meses depois, que "desligou e
    // não aconteceu nada". Sem rotas e sem api — não gateia, e o painel não a
    // oferece. Só some do banco quando alguém decidir migrar o dado.
    chave: "regularizacao",
    label: "Regularização (legado)",
    rotas: [],
    aliases: [],
    api: [],
    legado: true,
  },
] as const;

/**
 * As chaves que o painel do Operador OFERECE (liga/desliga) e que a adoção mede.
 * Legado fica de fora: não dá pra contratar o que não controla nada.
 */
export const MODULOS_VENDAVEIS = CATALOGO.filter((m) => !m.legado);

/** Chaves que vêm no plano base (não somam adicional na mensalidade). */
export const NUCLEO_CHAVES = CATALOGO.filter((m) => m.nucleo).map((m) => m.chave);

/** Chaves INCLUSAS na marca (05/09): escondem tela, não cobram, ficam fora dos presets. */
export const BASICO_CHAVES = CATALOGO.filter((m) => m.basico).map((m) => m.chave);

/**
 * O gate de MENU por módulo (nível 1), num lugar só — o Layout e os testes de paridade
 * chamam esta função. `modulos` null/undefined = empresa sem a coluna = vê tudo (legado);
 * com a coluna, o item de módulo só aparece com `true` explícito (deny-by-default, 05/09).
 * Item de núcleo (sem módulo) sempre passa.
 */
export function itemVisivelPelosModulos(modulo: string | null, modulos: Record<string, boolean> | null | undefined): boolean {
  if (!modulo) return true;
  if (modulos == null) return true;
  return modulos[modulo] === true;
}

/** Módulo → rotas do cliente. Formato que a medição de adoção consome. */
export const MODULO_ROTAS: Record<string, string[]> = Object.fromEntries(
  MODULOS_VENDAVEIS.map((m) => [m.chave, [...m.rotas, ...m.aliases]]).filter(([, r]) => r.length > 0),
);

/** Módulo → rótulo humano. */
export const MODULO_LABEL: Record<string, string> = Object.fromEntries(
  CATALOGO.map((m) => [m.chave, m.label]),
);

const POR_ROTA: Record<string, string> = Object.fromEntries(
  CATALOGO.flatMap((m) => [...m.rotas, ...m.aliases].map((r) => [r, m.chave])),
);

/**
 * A que módulo esta tela pertence? `null` = NÚCLEO, sempre visível (Painel,
 * Repositório, Sócios…) — fora da allow-list do grant do usuário.
 *
 * É a MESMA pergunta que o menu, a tela de Acessos e a adoção fazem. Antes cada
 * um respondia do seu jeito.
 */
export function moduloDaRota(to: string): string | null {
  return POR_ROTA[to] ?? null;
}
